/* Lunya Fabric Care: enhance server-rendered category links without changing Symmetry. */
(() => {
  class LunyaFabricNavigation extends HTMLElement {
    constructor() {
      super();
      this.frame = null;
      this.onChange = this.scheduleUpdate.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
    }

    connectedCallback() {
      // Allow a newly inserted theme-editor section to finish parsing its children.
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.isConnected || !this.closest('.lunya-brand')) return;
        this.nav = this.querySelector('.lunya-fabric-care__quick-nav');
        this.targets = [...this.querySelectorAll('.lunya-fabric-care__fabric')];
        if (!this.nav || !this.targets.length) return;
        this.links = [...this.nav.querySelectorAll('a')];
        this.scroller = this.querySelector('.lunya-fabric-care__nav-scroll');
        this.owner = this.parentElement;
        this.motion = matchMedia('(prefers-reduced-motion: reduce)');
        this.current = null;
        this.setAttribute('data-enhanced', '');
        this.owner.addEventListener('click', this.onClick);
        this.nav.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('scroll', this.onChange, { passive: true });
        window.addEventListener('resize', this.onChange);
        window.addEventListener('hashchange', this.onChange);
        this.resizeObserver = new ResizeObserver(this.onChange);
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.nav);
        // PageHeader publishes this offset after its own debounced resize.
        const root = document.documentElement;
        this.headerOffset = root.style.getPropertyValue('--theme-sticky-header-height');
        this.headerObserver = new MutationObserver(() => {
          const offset = root.style.getPropertyValue('--theme-sticky-header-height');
          if (offset === this.headerOffset) return;
          this.headerOffset = offset;
          this.scheduleUpdate();
        });
        this.headerObserver.observe(root, { attributes: true, attributeFilter: ['style'] });
        this.update();
      });
    }

    disconnectedCallback() {
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.owner?.removeEventListener('click', this.onClick);
      this.nav?.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('scroll', this.onChange);
      window.removeEventListener('resize', this.onChange);
      window.removeEventListener('hashchange', this.onChange);
      this.resizeObserver?.disconnect();
      this.headerObserver?.disconnect();
      this.removeAttribute('data-enhanced');
      this.removeAttribute('data-active');
      this.style.removeProperty('--fabric-nav-height');
      if (this.nav) {
        this.nav.removeAttribute('aria-hidden');
        this.nav.inert = false;
      }
    }

    scheduleUpdate() {
      if (this.frame !== null || !this.isConnected) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const height = this.nav.offsetHeight;
      const value = `${height}px`;
      if (this.style.getPropertyValue('--fabric-nav-height') !== value) {
        this.style.setProperty('--fabric-nav-height', value);
      }
      const navRect = this.nav.getBoundingClientRect();
      // Match the CSS anchor gap when page zoom scales bounding rectangles.
      const scale = height ? navRect.height / height : 1;
      const bottom = navRect.bottom;
      const line = bottom + 20 * scale;
      const rectangles = this.targets.map((target) => target.getBoundingClientRect());
      const first = rectangles[0];
      const last = rectangles[rectangles.length - 1];
      const active = first.top <= line + 1 && last.bottom > bottom;
      const width = this.nav.offsetWidth;
      let current = this.targets[0];
      rectangles.forEach((rectangle, index) => {
        if (rectangle.top <= line + 1) current = this.targets[index];
      });
      // Measure before changing active/ARIA state so a scroll tick does not
      // invalidate styles between panel measurements.
      const reveal = this.current !== current.id || this.navWidth !== width;
      const currentLink = reveal && this.links.find((link) => link.getAttribute('href') === `#${current.id}`);
      let delta = 0;
      if (currentLink) {
        const rect = currentLink.getBoundingClientRect();
        const view = this.scroller.getBoundingClientRect();
        if (rect.left < view.left || rect.right > view.right) {
          delta = rect.left < view.left || rect.width > view.width
            ? rect.left - view.left : rect.right - view.right;
        }
      }
      if (!active && this.nav.contains(document.activeElement)) {
        (first.top > line ? this.targets[0] : this.targets[this.targets.length - 1]).focus({ preventScroll: true });
      }
      if (this.hasAttribute('data-active') !== active) this.toggleAttribute('data-active', active);
      if (this.nav.getAttribute('aria-hidden') !== String(!active)) this.nav.setAttribute('aria-hidden', String(!active));
      if (this.nav.inert !== !active) this.nav.inert = !active;
      // Resize can clip an unchanged category. Ordinary scrolling should not
      // snap a row the shopper has deliberately scrolled horizontally.
      if (!reveal) return;
      this.navWidth = width;
      this.current = current.id;
      this.links.forEach((link) => {
        if (link.getAttribute('href') === `#${current.id}`) {
          if (link.getAttribute('aria-current') !== 'location') link.setAttribute('aria-current', 'location');
        } else if (link.hasAttribute('aria-current')) {
          link.removeAttribute('aria-current');
        }
      });
      // Reveal horizontally without scrolling the document or stealing focus.
      if (delta) this.scroller.scrollBy({ left: delta / scale, behavior: 'instant' });
    }

    handleClick(evt) {
      if (evt.defaultPrevented || evt.button > 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
      const link = evt.target.closest('a');
      const target = link && this.targets.find((panel) => link.getAttribute('href') === `#${panel.id}`);
      if (!target) return;
      evt.preventDefault();
      // The native document-level smooth-scroll handler does not know this extra bar.
      evt.stopPropagation();
      const href = link.getAttribute('href');
      if (location.hash !== href) history.pushState(history.state, '', href);
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: 'start', behavior: this.motion.matches ? 'instant' : 'smooth' });
      this.scheduleUpdate();
    }

    handleKeyDown(evt) {
      const index = this.links.indexOf(evt.target.closest('a'));
      if (index < 0) return;
      const rtl = getComputedStyle(this.nav).direction === 'rtl';
      let next;
      if (evt.key === 'Home') next = 0;
      if (evt.key === 'End') next = this.links.length - 1;
      if (evt.key === 'ArrowRight') next = Math.min(this.links.length - 1, Math.max(0, index + (rtl ? -1 : 1)));
      if (evt.key === 'ArrowLeft') next = Math.min(this.links.length - 1, Math.max(0, index + (rtl ? 1 : -1)));
      if (next === undefined) return;
      evt.preventDefault();
      this.links[next].focus({ preventScroll: true });
      this.links[next].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    }
  }

  if (!customElements.get('lunya-fabric-navigation')) {
    customElements.define('lunya-fabric-navigation', LunyaFabricNavigation);
  }
})();
